import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useOptimizedNotifications } from "@/hooks/useOptimizedNotifications";
import { useReferralCode } from "@/hooks/referrals/useReferralCode";
import { useCompanyLink } from "@/hooks/useCompanyLink";
import { useMyCompanies } from "@/hooks/useMyCompanies";
import { formatDistanceToNow } from "date-fns";
import { formatNotificationMessageForDisplay } from "@/utils/common";
import { openHelpResource } from "@/config/helpResources";
import { PRODUCTS_SERVICES_PATH } from "@/constants/publicPaths";
import {
  User,
  Settings,
  Shield,
  Bell,
  LogOut,
  Crown,
  HelpCircle,
  BookOpen,
  Users,
  ChevronRight,
  Sparkles,
  CheckCheck,
  Info,
  FileText,
  Scale,
  Gift,
  Copy,
} from "lucide-react";

interface UserProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A comprehensive, collapsible user profile and settings modal
 * Provides quick access to user info, settings, and actions
 */
export function UserProfileModal({ open, onOpenChange }: Readonly<UserProfileModalProps>) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { isPaid, subscriptionTier } = useSubscriptionStatus({ enabled: open });
  const { referralCode, shareVia } = useReferralCode({ enabled: open });
  const { companyLink } = useCompanyLink({ enabled: open });
  const { data: myCompanies } = useMyCompanies({ enabled: open });
  const [expandedSections, setExpandedSections] = useState<string[]>(["account"]);

  // Hide Upgrade and Referral for company users (HR or sponsored employees)
  const isCompanyHr = (myCompanies?.length ?? 0) > 0;
  const isCompanyEmployee = !!companyLink;
  const hideUpgradeAndReferral = isCompanyHr || isCompanyEmployee;

  // Fetch notifications
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useOptimizedNotifications({ enabled: open });

  // Fetch notifications when modal opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const handleSignOut = async () => {
    await signOut();
    onOpenChange(false);
    // Redirect to landing after logout
    // Use replace to prevent back button issues
    globalThis.location.replace("/");
  };

  // Get user initials for avatar fallback
  const getInitials = () => {
    const email = user?.email || "";
    const name = user?.user_metadata?.full_name || email;
    if (name.includes(" ")) {
      const parts = name.split(" ");
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get subscription badge variant
  const getSubscriptionBadge = () => {
    if (isPaid) {
      return (
        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
          <Crown className="mr-1 h-3 w-3" />
          {subscriptionTier || "Premium"}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="text-muted-foreground">
        Free Plan
      </Badge>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[400px] md:w-[440px] p-0 max-w-[100vw]">
        <ScrollArea className="h-full">
          <div className="flex flex-col h-full">
            {/* Header Section */}
            <SheetHeader className="px-6 pt-6 pb-4 space-y-0">
              <SheetTitle className="text-left">Profile & Settings</SheetTitle>
            </SheetHeader>

            {/* User Info Card */}
            <div className="px-6 pb-4">
              <div className="flex items-start space-x-4 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border">
                <Avatar className="h-16 w-16 border-2 border-white shadow-md">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-semibold text-lg">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg truncate">
                    {user?.user_metadata?.full_name || user?.user_metadata?.display_name || "User"}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {getSubscriptionBadge()}
                    <Badge variant="outline" className="text-xs">
                      ID: {user?.id?.slice(0, 8)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Collapsible Sections */}
            <div className="flex-1 px-6 py-4">
              <Accordion
                type="multiple"
                value={expandedSections}
                onValueChange={setExpandedSections}
                className="space-y-2"
              >
                {/* Account Actions */}
                <AccordionItem value="account" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Account</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1 pt-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left"
                        onClick={() => handleNavigation("/profile")}
                      >
                        <User className="mr-2 h-4 w-4" />
                        My Profile
                        <ChevronRight className="ml-auto h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left"
                        onClick={() => handleNavigation("/settings")}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                        <ChevronRight className="ml-auto h-4 w-4" />
                      </Button>
                      {user?.user_metadata?.role === "admin" && (
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-left"
                          onClick={() => handleNavigation("/admin")}
                        >
                          <Shield className="mr-2 h-4 w-4 text-orange-600" />
                          Admin Console
                          <ChevronRight className="ml-auto h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Referral Program - hidden for company users */}
                {!hideUpgradeAndReferral && (
                  <AccordionItem
                    value="referrals"
                    className="border rounded-lg px-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950 dark:to-green-950"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Gift className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium">Referral Program</span>
                        <Badge className="ml-auto mr-2 bg-emerald-500 text-white text-xs">
                          New
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-muted-foreground">
                          Share TellerWand and earn free Pro months!
                        </p>

                        <div className="flex gap-2 text-xs justify-center py-2">
                          <div className="text-center">
                            <div className="font-bold text-orange-600 text-lg">+2</div>
                            <div className="text-muted-foreground">For You</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-amber-600 text-lg">+1</div>
                            <div className="text-muted-foreground">For Friend</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-orange-500 text-lg">+1-3</div>
                            <div className="text-muted-foreground">On Upgrade</div>
                          </div>
                        </div>

                        {referralCode && (
                          <div className="space-y-2">
                            <div className="p-2 bg-white dark:bg-gray-900 rounded border">
                              <p className="text-xs text-muted-foreground mb-1">Your Code:</p>
                              <div className="flex items-center gap-2">
                                <code className="text-sm font-mono font-bold flex-1">
                                  {referralCode.custom_code || referralCode.code}
                                </code>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    shareVia("copy");
                                  }}
                                  className="h-7"
                                  aria-label="Copy referral link"
                                  data-testid="referral-copy-btn"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleNavigation("/referrals/dashboard")}
                              >
                                View Dashboard
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleNavigation("/referrals")}
                              >
                                Learn More
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Notifications */}
                <AccordionItem value="notifications" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2 w-full">
                      <Bell className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">Notifications</span>
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-auto mr-2 h-5 px-2 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      {/* Inline notifications preview */}
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        <div className="flex items-center justify-between px-2 pb-2 border-b">
                          <div className="text-sm font-medium text-muted-foreground">
                            Recent Notifications
                          </div>
                          {unreadCount > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAllAsRead();
                              }}
                            >
                              <CheckCheck className="h-3 w-3 mr-1" />
                              Mark all read
                            </Button>
                          )}
                        </div>

                        {notificationsLoading && (
                          <div className="text-sm text-muted-foreground text-center py-4">
                            Loading notifications...
                          </div>
                        )}
                        {!notificationsLoading && notifications.length === 0 && (
                          <div className="text-sm text-muted-foreground text-center py-8">
                            <Bell className="h-12 w-12 mx-auto mb-2 text-muted-foreground/30" />
                            <p className="font-medium">No notifications yet</p>
                            <p className="text-xs mt-1">You're all caught up!</p>
                          </div>
                        )}
                        {!notificationsLoading && notifications.length > 0 && (
                          <div className="space-y-1">
                            {notifications.slice(0, 10).map((notification) => (
                              <button
                                key={notification.id}
                                type="button"
                                className={`w-full text-left p-3 rounded-lg cursor-pointer transition-all duration-200 group ${
                                  notification.is_read
                                    ? "hover:bg-muted/50"
                                    : "bg-primary/10 hover:bg-primary/15 border border-primary/20"
                                }`}
                                onClick={() => {
                                  if (notification.is_read) {
                                    return;
                                  }
                                  markAsRead(notification.id);
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 transition-colors ${
                                      notification.is_read ? "bg-muted" : "bg-primary animate-pulse"
                                    }`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className="text-sm font-semibold text-foreground">
                                        {notification.title}
                                      </p>
                                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                                        {formatDistanceToNow(new Date(notification.created_at), {
                                          addSuffix: true,
                                        })}
                                      </p>
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-relaxed">
                                      {formatNotificationMessageForDisplay(notification.message)}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                            {notifications.length > 10 && (
                              <div className="text-xs text-center text-primary py-2 font-medium">
                                + {notifications.length - 10} more notifications
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="pt-2 border-t space-y-1">
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-left"
                          onClick={() => handleNavigation("/settings?section=preferences")}
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Notification settings
                          <ChevronRight className="ml-auto h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Subscription - hidden for company users */}
                {!isPaid && !hideUpgradeAndReferral && (
                  <AccordionItem
                    value="subscription"
                    className="border rounded-lg px-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950 dark:to-orange-950"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-yellow-600" />
                        <span className="font-medium">Upgrade to Premium</span>
                        <Sparkles className="h-4 w-4 text-yellow-600 ml-auto" />
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-muted-foreground">
                          Unlock advanced features, unlimited assets, family hub, and more!
                        </p>
                        <Button
                          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                          onClick={() => handleNavigation("/subscription")}
                        >
                          <Crown className="mr-2 h-4 w-4" />
                          Upgrade Now
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Help & Resources */}
                <AccordionItem value="help" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium">Help & Resources</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1 pt-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left"
                        onClick={() => handleNavigation("/contact-us")}
                      >
                        <HelpCircle className="mr-2 h-4 w-4" />
                        Help & Support
                        <ChevronRight className="ml-auto h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left"
                        onClick={() => handleNavigation("/about")}
                      >
                        <Info className="mr-2 h-4 w-4" />
                        About TellerWand
                        <ChevronRight className="ml-auto h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left"
                        onClick={() => {
                          openHelpResource("documentation", (path) => handleNavigation(path));
                        }}
                      >
                        <BookOpen className="mr-2 h-4 w-4" />
                        Documentation
                        <ChevronRight className="ml-auto h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left"
                        onClick={() => {
                          openHelpResource("community", (path) => handleNavigation(path));
                        }}
                      >
                        <Users className="mr-2 h-4 w-4" />
                        Community
                        <ChevronRight className="ml-auto h-4 w-4" />
                      </Button>

                      <Separator className="my-2" />

                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left text-xs text-muted-foreground"
                        onClick={() => handleNavigation("/terms")}
                      >
                        <FileText className="mr-2 h-3 w-3" />
                        Terms & Conditions
                        <ChevronRight className="ml-auto h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left text-xs text-muted-foreground"
                        onClick={() => handleNavigation("/privacy")}
                      >
                        <Scale className="mr-2 h-3 w-3" />
                        Privacy Policy
                        <ChevronRight className="ml-auto h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left text-xs text-muted-foreground"
                        onClick={() => handleNavigation("/refunds")}
                      >
                        <FileText className="mr-2 h-3 w-3" />
                        Refunds & Cancellations
                        <ChevronRight className="ml-auto h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left text-xs text-muted-foreground"
                        onClick={() => handleNavigation(PRODUCTS_SERVICES_PATH)}
                      >
                        <FileText className="mr-2 h-3 w-3" />
                        Products & Services
                        <ChevronRight className="ml-auto h-3 w-3" />
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <Separator />

            {/* Sign Out */}
            <div className="px-6 py-4">
              <Button variant="destructive" className="w-full" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 text-center text-xs text-muted-foreground">
              <p>TellerWand v2.0 • © {new Date().getFullYear()}</p>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
